import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Shield, Search, Download, RefreshCw, ChevronLeft, ChevronRight,
  Activity, User, Database, AlertTriangle, Eye, Filter, X,
} from "lucide-react";
import { securityAPI } from "../services/api";

const PAGE_SIZE = 50;

const KpiCard = ({ title, value, sub, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
    green: "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    purple: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400",
    orange: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}><Icon size={20} /></div>
      </div>
    </div>
  );
};

const DiffViewer = ({ oldValue, newValue }) => {
  const { t } = useTranslation();
  if (!oldValue && !newValue) return <span className="text-muted-foreground text-xs italic">—</span>;
  const fmt = (v) => v ? JSON.stringify(v, null, 2) : t("auditTrail.emptyValue");
  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div>
        <p className="font-semibold text-red-600 dark:text-red-400 mb-1">{t("auditTrail.before")}</p>
        <pre className="bg-red-50 dark:bg-red-950/30 p-2 rounded border border-red-200 dark:border-red-800 overflow-auto max-h-40">{fmt(oldValue)}</pre>
      </div>
      <div>
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">{t("auditTrail.after")}</p>
        <pre className="bg-green-50 dark:bg-green-950/30 p-2 rounded border border-green-200 dark:border-green-800 overflow-auto max-h-40">{fmt(newValue)}</pre>
      </div>
    </div>
  );
};

export default function AuditTrail() {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [facets, setFacets] = useState({ actions: [], entity_types: [] });
  const [expanded, setExpanded] = useState(null);

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const params = useMemo(() => {
    const p = { offset: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (search) p.search = search;
    if (actionFilter) p.action = actionFilter;
    if (entityFilter) p.entity_type = entityFilter;
    if (userFilter) p.user = userFilter;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  }, [page, search, actionFilter, entityFilter, userFilter, startDate, endDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await securityAPI.getAuditLogsPaginated(params);
      setLogs(r.items || []);
      setTotal(r.total || 0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    securityAPI.getAuditFacets().then(setFacets).catch(() => {});
  }, []);

  useEffect(() => {
    const statsParams = {};
    if (startDate) statsParams.start_date = startDate;
    if (endDate) statsParams.end_date = endDate;
    securityAPI.getAuditStats(statsParams).then(setStats).catch(() => {});
  }, [startDate, endDate]);

  const resetFilters = () => {
    setSearch(""); setActionFilter(""); setEntityFilter("");
    setUserFilter(""); setStartDate(""); setEndDate(""); setPage(0);
  };

  const exportCsv = () => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (actionFilter) qs.set("action", actionFilter);
    if (entityFilter) qs.set("entity_type", entityFilter);
    if (userFilter) qs.set("user", userFilter);
    if (startDate) qs.set("start_date", startDate);
    if (endDate) qs.set("end_date", endDate);
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API_URL}/v1/security/audit-logs/export.csv?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const activeFilters = [search, actionFilter, entityFilter, userFilter, startDate, endDate].filter(Boolean).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            {t("auditTrail.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("auditTrail.subtitle")}
          </p>
        </div>
        <button onClick={exportCsv}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90">
          <Download className="h-4 w-4" /> {t("auditTrail.exportCsv")}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard title={t("auditTrail.kpiTotal")} value={total.toLocaleString(i18n.language)} icon={Activity} color="blue" />
        <KpiCard title={t("auditTrail.kpiActions")} value={facets.actions.length} sub={t("auditTrail.kpiActionsSub")} icon={Database} color="purple" />
        <KpiCard title={t("auditTrail.kpiEntities")} value={facets.entity_types.length} sub={t("auditTrail.kpiEntitiesSub")} icon={Eye} color="orange" />
        <KpiCard title={t("auditTrail.kpiActors")} value={stats?.top_actors?.length || 0} sub={t("auditTrail.kpiActorsSub")} icon={User} color="green" />
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="h-4 w-4" /> {t("auditTrail.filters")}
            {activeFilters > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">{activeFilters}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFilters > 0 && (
              <button onClick={resetFilters} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground">
                <X className="h-3 w-3" /> {t("auditTrail.reset")}
              </button>
            )}
            <button onClick={load} className="text-xs flex items-center gap-1 px-3 py-1 rounded border hover:bg-muted">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> {t("auditTrail.refresh")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input type="text" placeholder={t("auditTrail.searchPlaceholder")} value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background" />
          </div>
          <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 text-sm border rounded-md bg-background">
            <option value="">{t("auditTrail.allActions")}</option>
            {facets.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={entityFilter} onChange={(e) => { setEntityFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 text-sm border rounded-md bg-background">
            <option value="">{t("auditTrail.allEntities")}</option>
            {facets.entity_types.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
            className="px-3 py-2 text-sm border rounded-md bg-background" placeholder={t("auditTrail.startDate")} />
          <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
            className="px-3 py-2 text-sm border rounded-md bg-background" placeholder={t("auditTrail.endDate")} />
        </div>
      </div>

      {/* Top actions / actors charts */}
      {stats && (stats.by_action?.length > 0 || stats.top_actors?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {stats.by_action?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm font-medium mb-3">{t("auditTrail.topActions")}</p>
              <div className="space-y-1.5">
                {stats.by_action.slice(0, 8).map((row) => {
                  const max = stats.by_action[0].count;
                  return (
                    <div key={row.action} className="flex items-center gap-2 text-xs">
                      <span className="w-40 truncate font-mono text-muted-foreground">{row.action}</span>
                      <div className="flex-1 h-5 bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-primary/60 rounded-sm" style={{ width: `${(row.count / max) * 100}%` }} />
                      </div>
                      <span className="w-12 text-right font-semibold">{row.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {stats.top_actors?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm font-medium mb-3">{t("auditTrail.topActors")}</p>
              <div className="space-y-1.5">
                {stats.top_actors.slice(0, 8).map((row) => {
                  const max = stats.top_actors[0].count;
                  return (
                    <div key={row.actor} className="flex items-center gap-2 text-xs">
                      <span className="w-40 truncate text-muted-foreground">{row.actor}</span>
                      <div className="flex-1 h-5 bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-green-500/60 rounded-sm" style={{ width: `${(row.count / max) * 100}%` }} />
                      </div>
                      <span className="w-12 text-right font-semibold">{row.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-40" />
            {t("auditTrail.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 border-b">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground w-40">{t("auditTrail.colTimestamp")}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">{t("auditTrail.colActor")}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">{t("auditTrail.colAction")}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">{t("auditTrail.colEntity")}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">{t("auditTrail.colIp")}</th>
                  <th className="px-4 py-2.5 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-muted/20 cursor-pointer"
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                      <td className="px-4 py-2.5 font-mono text-xs">{new Date(log.created_at).toLocaleString(i18n.language)}</td>
                      <td className="px-4 py-2.5 text-xs">{log.actor_email || <span className="text-muted-foreground italic">{t("auditTrail.system")}</span>}</td>
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{log.action}</span>
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {log.entity_type ? (
                          <span>{log.entity_type}{log.entity_id ? <span className="text-muted-foreground"> #{log.entity_id}</span> : null}</span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{log.ip_address || "—"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        <Eye className="h-3.5 w-3.5" />
                      </td>
                    </tr>
                    {expanded === log.id && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-muted/20 border-b">
                          <div className="space-y-3">
                            {log.user_agent && (
                              <div className="text-xs">
                                <p className="font-semibold text-muted-foreground mb-0.5">{t("auditTrail.userAgent")}</p>
                                <p className="font-mono break-all">{log.user_agent}</p>
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-muted-foreground text-xs mb-1">{t("auditTrail.diff")}</p>
                              <DiffViewer oldValue={log.old_value} newValue={log.new_value} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between text-sm">
            <p className="text-muted-foreground text-xs">
              {t("auditTrail.pageRange", { from: page * PAGE_SIZE + 1, to: Math.min((page + 1) * PAGE_SIZE, total), total: total.toLocaleString(i18n.language) })}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 rounded border disabled:opacity-30 hover:bg-muted">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs">{t("auditTrail.pageOf", { page: page + 1, total: totalPages })}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 rounded border disabled:opacity-30 hover:bg-muted">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
